import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonLabel,
  IonIcon,
  IonBackButton,
  IonButtons,
  IonToast,
  IonSpinner,
} from '@ionic/react';
import { save, camera, businessOutline, documentTextOutline, globeOutline } from 'ionicons/icons';
import { z } from 'zod';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';
import {
  useEmployerProfile,
  useUpdateEmployerProfile,
  useCreateEmployerProfile,
  useSessionInfo,
  useUploadFile,
} from '@/lib/api/hooks';
import type { EmployerProfile } from '@/types';
import './EmployerProfileManagement.css';

const schema = z.object({
  companyName: z.string().min(2, 'Company name is required'),
  industry: z.string().min(1, 'Select an industry'),
  companySize: z.enum(['1-10', '11-50', '51-200', '201-500', '500+']),
  description: z.string().optional(),
  website: z.string().url('Enter a valid URL').optional().or(z.literal('')),
});

interface Form {
  companyName: string;
  industry: string;
  companySize: string;
  description: string;
  website: string;
}

const INDUSTRIES = [
  'Construction', 'Mining', 'Manufacturing', 'Logistics', 'Retail', 'Hospitality',
  'Healthcare', 'Education', 'IT', 'Finance', 'Agriculture', 'Energy',
  'Transport', 'Telecommunications', 'Real Estate', 'Other',
];

export function EmployerProfileManagement() {
  const history = useHistory();
  const { data: sessionEnvelope } = useSessionInfo();
  const session = sessionEnvelope?.data;
  const tenantId = session?.currentTenant?.id ?? '';

  const [employerProfileId, setEmployerProfileId] = useState<string>('');

  const { data: employersEnvelope, isLoading: employersLoading } = useEmployerProfile(employerProfileId);

  useEffect(() => {
    if (!tenantId || employerProfileId) return;
    const fetchEmployerId = async () => {
      try {
        const { api } = await import('@/lib/api/client');
        const res = await api.get<{ success: boolean; data: EmployerProfile[] }>(
          `/workseeking/employers`,
          { params: { tenantId } },
        );
        const myProfile = res.data?.[0];
        if (myProfile?._id) {
          setEmployerProfileId(myProfile._id);
        }
      } catch (err) {
        console.warn('[employer-profile] fetchEmployerId failed:', err);
      }
    };
    fetchEmployerId();
  }, [tenantId, employerProfileId, session?.userId]);

  const profile = employersEnvelope?.data;
  const updateProfile = useUpdateEmployerProfile();
  const createProfile = useCreateEmployerProfile();
  const uploadFile = useUploadFile();
  const isCreating = !employerProfileId && !employersLoading;

  const [form, setForm] = useState<Form>({
    companyName: '',
    industry: '',
    companySize: '1-10',
    description: '',
    website: '',
  });

  const [initialized, setInitialized] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (profile && !initialized) {
      setForm({
        companyName: profile.companyName ?? '',
        industry: profile.industry ?? '',
        companySize: profile.companySize ?? '1-10',
        description: profile.description ?? '',
        website: profile.website ?? '',
      });
      setInitialized(true);
    }
  }, [profile, initialized]);

  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setToast({ msg: 'Max 5 MB', color: 'danger' }); return; }
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      setToast({ msg: 'Accepts JPEG, PNG, WebP, SVG', color: 'danger' }); return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const r = schema.safeParse(form);
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) e[String(issue.path[0])] = issue.message;
      setErrors(e);
      return;
    }
    setErrors({});

    let logoRef: { type: 'filereference'; id: string } | undefined = profile?.logoRef ?? undefined;

    if (selectedFile) {
      try {
        const uploadRes = await uploadFile.mutateAsync({
          file: selectedFile,
          subsystem: 'Identity',
          category: 'PersonalDocument',
          ownerId: employerProfileId || undefined,
          ownerType: 'employerprofile',
        });
        logoRef = uploadRes.data.fileRef;
      } catch (err: any) {
        setToast({ msg: err.message || 'Logo upload failed', color: 'danger' });
        return;
      }
    }

    if (isCreating) {
      createProfile.mutate(
        { tenantId, data: { ...form, logoRef } as Partial<EmployerProfile> },
        {
          onSuccess: (res) => {
            const newId = (res as unknown as { data?: { id?: string } })?.data?.id ?? '';
            if (newId) setEmployerProfileId(newId);
            setToast({ msg: 'Profile created', color: 'success' });
            setInitialized(false);
          },
          onError: (err: Error) => {
            setToast({ msg: err.message || 'Failed to create profile', color: 'danger' });
          },
        },
      );
    } else if (employerProfileId) {
      updateProfile.mutate(
        { id: employerProfileId, data: { ...form, logoRef } as Partial<EmployerProfile> },
        {
          onSuccess: () => {
            setToast({ msg: 'Profile saved', color: 'success' });
            setTimeout(() => history.goBack(), 800);
          },
          onError: (err: Error) => {
            setToast({ msg: err.message || 'Failed to save profile', color: 'danger' });
          },
        },
      );
    }
  };

  const isLoading = employersLoading || updateProfile.isPending || createProfile.isPending;

  if (employersLoading && !initialized) {
    return (
      <IonPage>
        <NoindexSeo />
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/employer" /></IonButtons>
            <IonTitle>Employer Profile</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding epm-page">
          <div className="epm-loading">
            <IonSpinner name="crescent" />
            <p className="epm-loading-text">Loading profile...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/employer" /></IonButtons>
          <IonTitle>{isCreating ? 'Create Employer Profile' : 'Employer Profile'}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} disabled={isLoading}>
              {isLoading ? <IonSpinner name="crescent" style={{ width: 20, height: 20 }} /> : <IonIcon icon={save} />}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding epm-page">

        {/* Logo */}
        <div className="epm-logo-section" onClick={() => fileInputRef.current?.click()}>
          <div className="epm-logo-ring">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="epm-logo-img" />
            ) : (
              <div className="epm-logo-placeholder">
                <IonIcon icon={camera} />
              </div>
            )}
            <div className="epm-logo-badge">
              <IonIcon icon={camera} />
            </div>
          </div>
          <span className="epm-logo-label">{logoPreview ? 'Change logo' : 'Add logo'}</span>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" hidden onChange={handleLogoSelect} />
        </div>

        {/* Company Details */}
        <FormSection icon={businessOutline} title="Company Details">
          <FormField label="Company name" error={errors.companyName}>
            <IonInput
              placeholder="Enter company name"
              value={form.companyName}
              onIonInput={(e) => set({ companyName: e.detail.value! })}
            />
          </FormField>
          <FormField label="Industry" error={errors.industry}>
            <IonSelect value={form.industry} onIonChange={(e) => set({ industry: e.detail.value })}>
              <IonSelectOption value="">Select industry</IonSelectOption>
              {INDUSTRIES.map((i) => <IonSelectOption key={i} value={i}>{i}</IonSelectOption>)}
            </IonSelect>
          </FormField>
          <FormField label="Company size" error={undefined}>
            <IonSelect value={form.companySize} onIonChange={(e) => set({ companySize: e.detail.value })}>
              {['1-10', '11-50', '51-200', '201-500', '500+'].map((s) => <IonSelectOption key={s} value={s}>{s}</IonSelectOption>)}
            </IonSelect>
          </FormField>
        </FormSection>

        {/* About */}
        <FormSection icon={documentTextOutline} title="About">
          <FormField label="Description" error={undefined}>
            <IonTextarea
              placeholder="Tell clients about your company"
              value={form.description}
              onIonInput={(e) => set({ description: e.detail.value! })}
              rows={3}
            />
          </FormField>
        </FormSection>

        {/* Online Presence */}
        <FormSection icon={globeOutline} title="Online Presence">
          <FormField label="Website" error={errors.website}>
            <IonInput
              placeholder="https://..."
              value={form.website}
              onIonInput={(e) => set({ website: e.detail.value! })}
            />
          </FormField>
        </FormSection>

        {/* Save button */}
        <IonButton expand="block" className="epm-save-btn" onClick={handleSave} disabled={isLoading}>
          {isCreating ? 'Create profile' : 'Save profile'}
        </IonButton>

        {!isCreating && (
          <IonButton expand="block" fill="outline" className="epm-link-btn" onClick={() => {
            const leId = profile?.legalEntity?.id;
            history.push(leId ? `/profile/legal-entity/${leId}` : '/profile/legal-entity');
          }}>
            Edit Company & Contact Details
          </IonButton>
        )}

        <AppFooter />
      </IonContent>
      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}

function FormSection({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="epm-section">
      <div className="epm-section-header">
        <IonIcon icon={icon} className="epm-section-icon" />
        <h3 className="epm-section-title">{title}</h3>
      </div>
      <div className="epm-section-body">
        {children}
      </div>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="epm-field">
      <IonLabel className="epm-field-label">{label}</IonLabel>
      {children}
      {error && <span className="epm-field-error">{error}</span>}
    </div>
  );
}
